import type { ListItem } from '@lista/shared-validation';

/**
 * Drag-and-drop ordering shared by web and mobile (FR-LST-06, FR-ITM reorder, ADR-0008).
 * The server stores fractional positions; clients only say which neighbours a moved
 * entry now sits between, so a move writes one row and never renumbers the rest.
 */
export interface Neighbours {
  /** The entry that now comes right before the moved one (null = it is first). */
  afterId: string | null;
  /** The entry that now comes right after the moved one (null = it is last). */
  beforeId: string | null;
}

interface WithId {
  id: string;
}

/**
 * Moves `id` to index `to` of the visible order (array-move semantics: remove, then
 * insert at `to`). Returns the new order and the neighbours to send to the server, or
 * null when nothing moves.
 */
export function moveToIndex<T extends WithId>(
  visible: readonly T[],
  id: string,
  to: number,
): { order: T[]; neighbours: Neighbours } | null {
  const from = visible.findIndex((x) => x.id === id);
  if (from < 0 || visible.length < 2) return null;
  const target = Math.max(0, Math.min(to, visible.length - 1));
  if (target === from) return null;
  const order = [...visible];
  const [moved] = order.splice(from, 1);
  order.splice(target, 0, moved!);
  return {
    order,
    neighbours: {
      afterId: order[target - 1]?.id ?? null,
      beforeId: order[target + 1]?.id ?? null,
    },
  };
}

/**
 * A move on the dashboard. Pinned lists always come first, ordered by when they were
 * pinned, so they cannot be dragged and nothing can be dropped above them: the target is
 * kept inside the unpinned part and the neighbours sent to the server are unpinned lists
 * only (the server compares their stored positions, which pinned lists do not follow).
 */
export function moveListToIndex<T extends WithId & { pinned?: boolean }>(
  visible: readonly T[],
  id: string,
  to: number,
): { neighbours: Neighbours } | null {
  const moved = visible.find((x) => x.id === id);
  if (!moved || moved.pinned) return null;
  const firstFree = visible.findIndex((x) => !x.pinned);
  const free = visible.filter((x) => !x.pinned);
  const result = moveToIndex(free, id, Math.max(to, firstFree) - firstFree);
  return result ? { neighbours: result.neighbours } : null;
}

/**
 * Applies a move to a full (possibly unfiltered) cached array: the entry is placed right
 * after `afterId`, or right before `beforeId` when it moved to the front. Entries that
 * are not visible keep their relative order, matching what the server will do.
 */
export function placeBetween<T extends WithId>(
  all: readonly T[],
  id: string,
  { afterId, beforeId }: Neighbours,
): T[] {
  const moved = all.find((x) => x.id === id);
  if (!moved) return [...all];
  const rest = all.filter((x) => x.id !== id);
  let at = afterId !== null ? rest.findIndex((x) => x.id === afterId) + 1 : -1;
  if (at <= 0 && beforeId !== null) {
    const before = rest.findIndex((x) => x.id === beforeId);
    at = before >= 0 ? before : at;
  }
  if (at < 0) at = afterId === null ? 0 : rest.length;
  rest.splice(at, 0, moved);
  return rest;
}

/** How the open lines of a list are shown (the bought lines always come last). */
export type ItemOrder = 'category' | 'custom' | 'createdAt' | 'updatedAt';
export const ITEM_ORDERS: readonly ItemOrder[] = ['category', 'custom', 'createdAt', 'updatedAt'];

export function isItemOrder(value: unknown): value is ItemOrder {
  return typeof value === 'string' && (ITEM_ORDERS as readonly string[]).includes(value);
}

/**
 * Open lines in the chosen order. `custom` keeps the server order (position, which the
 * user sets by dragging); the date orders show the newest first. `category` returns the
 * custom order too - the caller groups it.
 */
export function orderOpenItems(items: readonly ListItem[], order: ItemOrder): ListItem[] {
  const open = items.filter((i) => !i.purchased);
  if (order === 'createdAt' || order === 'updatedAt') {
    return [...open].sort((a, b) => (a[order] < b[order] ? 1 : a[order] > b[order] ? -1 : 0));
  }
  return open;
}
